/**
 * Generates a consistent avatar color based on a user identifier (email, ID, or name).
 * The same identifier will always produce the same color, ensuring consistency across
 * different screens and sessions.
 */

export interface AvatarColor {
  bg: string;
  text: string;
}

const AVATAR_COLORS: AvatarColor[] = [
  { bg: "#6366f1", text: "#ffffff" }, // Indigo
  { bg: "#ec4899", text: "#ffffff" }, // Pink
  { bg: "#f59e0b", text: "#ffffff" }, // Amber
  { bg: "#8b5cf6", text: "#ffffff" }, // Purple
  { bg: "#10b981", text: "#ffffff" }, // Emerald
  { bg: "#06b6d4", text: "#ffffff" }, // Cyan
  { bg: "#ef4444", text: "#ffffff" }, // Red
  { bg: "#3b82f6", text: "#ffffff" }, // Blue
  { bg: "#f97316", text: "#ffffff" }, // Orange
  { bg: "#14b8a6", text: "#ffffff" }, // Teal
  { bg: "#a855f7", text: "#ffffff" }, // Violet
  { bg: "#84cc16", text: "#ffffff" }, // Lime
];

/**
 * Simple hash function that converts a string to a consistent number.
 * This ensures the same string always produces the same hash value.
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Returns a consistent avatar color for a given user identifier.
 * The color is determined by hashing the identifier and mapping it to
 * one of the predefined color palettes.
 *
 * @param identifier - A unique identifier for the user (preferably email or ID)
 * @returns An object with background and text colors for the avatar
 *
 * @example
 * const color = getAvatarColor('user@example.com');
 * // Returns { bg: "#6366f1", text: "#ffffff" }
 */
export function getAvatarColor(identifier: string): AvatarColor {
  if (!identifier || identifier.trim() === "") {
    return AVATAR_COLORS[0];
  }

  const hash = hashString(identifier.toLowerCase().trim());
  const colorIndex = hash % AVATAR_COLORS.length;
  return AVATAR_COLORS[colorIndex];
}




